import { useMemo, useState, useCallback, useEffect } from "react";
import {
  ICategorizableItem,
  INomenclatureCategory,
} from "../models/categorizedNomenclatureModels";

interface UseCategorizedSearchResult<
  TItem extends ICategorizableItem,
  TCategory extends INomenclatureCategory
> {
  searchValue: string;
  isSearching: boolean;
  onSearch: (value: string) => void;
  filteredCategories: TCategory[];
  filteredItemsByCategory: Record<number | "uncategorized", TItem[]>;
  searchExpandedIndexes: number[] | null;
}

export function useCategorizedSearch<
  TItem extends ICategorizableItem,
  TCategory extends INomenclatureCategory
>(
  categories: TCategory[],
  itemsByCategory: Record<number | "uncategorized", TItem[]>,
  enabled: boolean
): UseCategorizedSearchResult<TItem, TCategory> {
  const [searchValue, setSearchValue] = useState("");

  const onSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const isSearching = enabled && searchValue.trim().length > 0;

  const activeCategories = useMemo(
    () => categories.filter((c) => !c.isDeleted),
    [categories]
  );

  const { filteredCategories, filteredItemsByCategory, searchExpandedIndexes } =
    useMemo(() => {
      if (!isSearching) {
        return {
          filteredCategories: categories,
          filteredItemsByCategory: itemsByCategory,
          searchExpandedIndexes: null,
        };
      }

      const normalizedSearch = searchValue
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

      const newItemsByCategory: Record<number | "uncategorized", TItem[]> = {
        uncategorized: [],
      };

      const matchingCategoryIds = new Set<number | "uncategorized">();

      // Filter items in each category
      activeCategories.forEach((cat) => {
        const categoryItems = itemsByCategory[cat.id] ?? [];
        const activeItems = categoryItems.filter((i) => !i.isDeleted);
        const matchingItems = activeItems.filter((item) =>
          flattenObjectValues(item).includes(normalizedSearch)
        );

        newItemsByCategory[cat.id] = matchingItems;

        if (matchingItems.length > 0) {
          matchingCategoryIds.add(cat.id);
        }
      });

      // Filter uncategorized items
      const uncategorizedItems = itemsByCategory.uncategorized ?? [];
      const activeUncategorized = uncategorizedItems.filter(
        (i) => !i.isDeleted
      );
      const matchingUncategorized = activeUncategorized.filter((item) =>
        flattenObjectValues(item).includes(normalizedSearch)
      );

      newItemsByCategory.uncategorized = matchingUncategorized;

      if (matchingUncategorized.length > 0) {
        matchingCategoryIds.add("uncategorized");
      }

      // Build filtered categories (only those with matching items)
      const filtered = categories.filter(
        (cat) => !cat.isDeleted && matchingCategoryIds.has(cat.id)
      );

      // Build expanded indexes — all matching tabs should be expanded
      const expandedIndexes: number[] = [];
      let tabIndex = 0;

      filtered.forEach(() => {
        expandedIndexes.push(tabIndex);
        tabIndex++;
      });

      // Account for uncategorized tab
      if (matchingCategoryIds.has("uncategorized")) {
        expandedIndexes.push(tabIndex);
      }

      return {
        filteredCategories: filtered,
        filteredItemsByCategory: newItemsByCategory,
        searchExpandedIndexes: expandedIndexes,
      };
    }, [
      isSearching,
      searchValue,
      categories,
      activeCategories,
      itemsByCategory,
    ]);

  return {
    searchValue,
    isSearching,
    onSearch,
    filteredCategories,
    filteredItemsByCategory,
    searchExpandedIndexes,
  };
}

function flattenObjectValues(obj: any): string {
  const values: string[] = [];

  const extractValues = (val: any) => {
    if (val == null) return;

    if (typeof val === "object") {
      for (const key in val) {
        extractValues(val[key]);
      }
    } else if (typeof val === "string" && isIsoDate(val)) {
      values.push(formatDate(val));
    } else {
      values.push(String(val));
    }
  };

  extractValues(obj);
  return values.join(" ").replace(/\s+/g, " ").toLowerCase();
}

function isIsoDate(val: string): boolean {
  const isoDateRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|([+-]\d{2}:\d{2}))?$/;
  return isoDateRegex.test(val);
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  const dd = String(date.getDate()).padStart(2, "0");
  const MM = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${MM}.${yyyy} ${hh}:${mm}`;
}
